import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getAIMatchById } from '../services/api';
import Loading from '../components/Loading';

const AIExplanationPage = () => {
  const { matchId } = useParams();
  const [match, setMatch] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadMatch = async () => {
      setLoading(true);
      const result = await getAIMatchById(matchId);
      if (result.success && result.match) {
        setMatch(result.match);
      }
      setLoading(false);
    };

    if (matchId) {
      loadMatch();
    }
  }, [matchId]);

  if (loading) {
    return <Loading message="Loading AI match analysis..." />;
  }

  if (!match) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Match Not Found</h2>
          <Link to="/dashboard" className="text-blue-600 hover:underline">
            Back to Dashboard
          </Link>
        </div>
      </div>
    );
  }

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 75) return 'text-blue-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 90) return 'bg-green-100';
    if (score >= 75) return 'bg-blue-100';
    if (score >= 60) return 'bg-yellow-100';
    return 'bg-red-100';
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-3xl font-bold text-gray-800">
              AI Match Analysis
            </h1>
            <div className={`text-5xl font-bold ${getScoreColor(match.matchScore)}`}>
              {match.matchScore}%
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-700 mb-1">Job Seeker</h3>
              <p className="text-xl text-gray-800">{match.jobSeekerName}</p>
            </div>
            <div className="bg-blue-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-700 mb-1">Position</h3>
              <p className="text-xl text-gray-800">{match.jobTitle}</p>
              <p className="text-sm text-gray-600">{match.companyName}</p>
            </div>
          </div>
        </div>

        {/* Match Score Breakdown */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            Match Score Breakdown
          </h2>

          <div className="space-y-6">
            {/* Skills Match */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-800">Skills Match</h3>
                <span className={`text-xl font-bold ${getScoreColor(match.explanation.skillsMatch.score)}`}>
                  {match.explanation.skillsMatch.score}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full ${getScoreBg(match.explanation.skillsMatch.score)} ${getScoreColor(match.explanation.skillsMatch.score)}`}
                  style={{ width: `${match.explanation.skillsMatch.score}%` }}
                ></div>
              </div>
              <p className="text-gray-700">{match.explanation.skillsMatch.details}</p>
            </div>

            {/* Experience Match */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-800">Experience Match</h3>
                <span className={`text-xl font-bold ${getScoreColor(match.explanation.experienceMatch.score)}`}>
                  {match.explanation.experienceMatch.score}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full ${getScoreBg(match.explanation.experienceMatch.score)} ${getScoreColor(match.explanation.experienceMatch.score)}`}
                  style={{ width: `${match.explanation.experienceMatch.score}%` }}
                ></div>
              </div>
              <p className="text-gray-700">{match.explanation.experienceMatch.details}</p>
            </div>

            {/* Culture Match */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-800">Culture Match</h3>
                <span className={`text-xl font-bold ${getScoreColor(match.explanation.cultureMatch.score)}`}>
                  {match.explanation.cultureMatch.score}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full ${getScoreBg(match.explanation.cultureMatch.score)} ${getScoreColor(match.explanation.cultureMatch.score)}`}
                  style={{ width: `${match.explanation.cultureMatch.score}%` }}
                ></div>
              </div>
              <p className="text-gray-700">{match.explanation.cultureMatch.details}</p>
            </div>

            {/* Salary Match */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-800">Salary Match</h3>
                <span className={`text-xl font-bold ${getScoreColor(match.explanation.salaryMatch.score)}`}>
                  {match.explanation.salaryMatch.score}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full ${getScoreBg(match.explanation.salaryMatch.score)} ${getScoreColor(match.explanation.salaryMatch.score)}`}
                  style={{ width: `${match.explanation.salaryMatch.score}%` }}
                ></div>
              </div>
              <p className="text-gray-700">{match.explanation.salaryMatch.details}</p>
            </div>

            {/* Location Match */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-800">Location Match</h3>
                <span className={`text-xl font-bold ${getScoreColor(match.explanation.locationMatch.score)}`}>
                  {match.explanation.locationMatch.score}%
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 mb-2">
                <div
                  className={`h-3 rounded-full ${getScoreBg(match.explanation.locationMatch.score)} ${getScoreColor(match.explanation.locationMatch.score)}`}
                  style={{ width: `${match.explanation.locationMatch.score}%` }}
                ></div>
              </div>
              <p className="text-gray-700">{match.explanation.locationMatch.details}</p>
            </div>
          </div>
        </div>

        {/* Strengths */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Key Strengths
          </h2>
          <div className="space-y-3">
            {match.explanation.strengths.map((strength, index) => (
              <div key={index} className="flex items-start">
                <span className="text-green-600 text-xl mr-3">✓</span>
                <p className="text-gray-700">{strength}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Considerations */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Things to Consider
          </h2>
          <div className="space-y-3">
            {match.explanation.considerations.map((consideration, index) => (
              <div key={index} className="flex items-start">
                <span className="text-yellow-600 text-xl mr-3">!</span>
                <p className="text-gray-700">{consideration}</p>
              </div>
            ))}
          </div>
        </div>

        {/* AI Insight */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg shadow-lg p-8">
          <div className="flex items-start">
            <div className="text-4xl mr-4">🤖</div>
            <div>
              <h3 className="text-xl font-bold mb-2">AI Recommendation</h3>
              <p className="text-blue-100">
                Based on our comprehensive analysis, this is a{' '}
                <span className="font-bold">
                  {match.matchScore >= 90 ? 'excellent' : match.matchScore >= 75 ? 'strong' : 'good'}
                </span>
                {' '}match. We recommend{' '}
                {match.matchScore >= 85
                  ? 'proceeding with the application immediately.'
                  : 'reviewing the considerations and proceeding if they align with your expectations.'}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-6 flex space-x-4">
          <Link
            to={`/company/${match.companyId}`}
            className="flex-1 bg-blue-600 text-white text-center py-3 rounded-lg font-semibold hover:bg-blue-700"
          >
            View Company Profile
          </Link>
          <Link
            to="/dashboard"
            className="flex-1 bg-gray-300 text-gray-800 text-center py-3 rounded-lg font-semibold hover:bg-gray-400"
          >
            Back to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default AIExplanationPage;
